import React, { useEffect, useRef, useState } from "react";
import {
  collection,
  limit,
  onSnapshot,
  orderBy,
  query,
} from "firebase/firestore";
import { db } from "../../../firebase.js";
import {
  ChatRoomMessages,
  ChatRoomMessageRow,
  ChatRoomInputRow,
  ButtonWrapper,
} from "../styles/Game.styles.js";
import { Input, ButtonFilled, Body } from "../../../styleguide/atoms/index.js";

export function ChatRoom({
  gameId,
  me,
  subcollection,
  postMessage,
  inputId,
  placeholder = "Type a message",
  ariaLabel,
  disabled,
}) {
  const [msgs, setMsgs] = useState([]);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const lastSentAtRef = useRef(0);
  const lastSentTextRef = useRef("");
  const scrollRef = useRef(null);

  // Subscribe to messages
  useEffect(() => {
    if (!gameId) return;

    const q = query(
      collection(db, "games", gameId, subcollection),
      orderBy("at", "asc"),
      limit(50)
    );

    const unsub = onSnapshot(
      q,
      (snap) => {
        const list = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        setMsgs(list);

        // Auto-scroll to bottom *after* DOM updates
        requestAnimationFrame(() => {
          if (scrollRef.current) {
            scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
          }
        });
      },
      (err) => {
        console.error(`${subcollection} subscription error:`, err);
      }
    );

    return () => unsub();
  }, [gameId, subcollection]);

  async function send() {
    const t = text.replace(/\s+/g, " ").trim();
    if (!t || sending || !gameId) return;

    const now = Date.now();

    // Simple spam/“oops double hit Enter” guard
    if (t === lastSentTextRef.current && now - lastSentAtRef.current < 1200) {
      return;
    }

    setSending(true);
    try {
      await postMessage(gameId, t);
      setText("");
      lastSentTextRef.current = t;
      lastSentAtRef.current = now;
    } catch (e) {
      console.error(e);
      alert(e?.message || "Failed to send message");
    } finally {
      setSending(false);
    }
  }

  function onKeyDown(e) {
    // Enter to send, Shift+Enter for newline if you ever allow multiline
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  }

  return (
    <>
      <ChatRoomMessages
        ref={scrollRef}
        aria-live="polite"
        aria-label={ariaLabel}
      >
        {msgs.length === 0 ? (
          <Body>No messages yet...</Body>
        ) : (
          msgs.map((m) => (
            <ChatRoomMessageRow key={m.id} isMine={m.playerId === me?.id}>
              <Body>
                <b>{m.playerName || "Unknown"}:</b> {m.text}
              </Body>
            </ChatRoomMessageRow>
          ))
        )}
      </ChatRoomMessages>

      {!disabled ? (
        <ChatRoomInputRow>
          <Input
            id={inputId}
            placeholder={placeholder}
            value={text}
            disabled={sending}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={onKeyDown}
            maxLength={80}
          />
          <ButtonWrapper>
            <ButtonFilled
              text={sending ? "Sending…" : "Send"}
              onClick={send}
              disabled={sending || !text.trim()}
            />
          </ButtonWrapper>
        </ChatRoomInputRow>
      ) : null}
    </>
  );
}
